// Ausências da própria pessoa em "Meu cadastro" (`/me/person/absences`; REQ-PPL-012; Parte 6.2/6.5): férias, licença,
// afastamento, treinamento, viagem ou outro, com período inclusivo, horas opcionais em par e `BlocksScheduling` (padrão: sim).
// A ausência é da PESSOA (vale para todos os vínculos). Antes de gravar dá para ver a PRÉVIA DE IMPACTO nas consultas
// confirmadas — enquanto o agendamento não existir o servidor responde `schedulingAvailable=false` e a tela diz isso, em vez
// de sugerir que nada é afetado. A remoção é lógica e auditada (razão fixa `PPL_SELF_ABSENCE`); ausência sobre consulta
// confirmada gera item de exceção, nunca cancela.
import { zodResolver } from '@hookform/resolvers/zod';
import type { NamespaceKey } from '@giyus/i18n';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  NativeCheckbox,
  NativeSelect,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@giyus/ui';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ProblemAlert } from '../../../components';
import { Field } from '../../authz/components/Fields';
import {
  ABSENCE_KINDS,
  type Absence,
  type AbsenceImpact,
  useCreateMyAbsence,
  useMyAbsences,
  usePreviewMyAbsenceImpact,
  useRemoveMyAbsence,
} from '../api/queries';
import { civilToday, useCivilDate } from '../lib/civilDateDisplay';
import {
  type AbsenceFormValues,
  absenceSchema,
  emptyAbsenceForm,
  shortTime,
  toAbsenceInput,
} from '../lib/selfService';

export function MyAbsencesCard() {
  const { t } = useTranslation('people');
  const civil = useCivilDate();
  const [includePast, setIncludePast] = useState(false);
  // "Próximas ausências": a partir de HOJE na data civil LOCAL (fuso do navegador), não da UTC — depois das 21h no Brasil a UTC já é
  // o dia seguinte e a ausência que termina hoje sumiria da lista.
  const today = civilToday(null);
  const range = useMemo(() => (includePast ? {} : { from: today }), [includePast, today]);
  const absences = useMyAbsences(range);
  const preview = usePreviewMyAbsenceImpact();
  const create = useCreateMyAbsence();
  const remove = useRemoveMyAbsence();
  const [impact, setImpact] = useState<{
    readonly saved: boolean;
    readonly impact: AbsenceImpact;
  } | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Absence | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<AbsenceFormValues>({
    resolver: zodResolver(absenceSchema),
    defaultValues: emptyAbsenceForm(),
  });
  const translate = (text: string | undefined): string | undefined =>
    text ? t(text as NamespaceKey<'people'>) : undefined;

  return (
    <section
      className="flex flex-col gap-4 rounded-card border p-4"
      aria-labelledby="my-absences-title"
      data-testid="my-absences"
    >
      <div>
        <h2 id="my-absences-title" className="text-title font-semibold">
          {t('me.absences.title')}
        </h2>
        <p className="text-body text-muted-foreground">{t('me.absences.description')}</p>
      </div>

      <form
        className="flex flex-col gap-3"
        noValidate
        data-testid="my-absence-form"
        onSubmit={(event) => {
          setMessage(null);
          void handleSubmit(async (values) => {
            try {
              const saved = await create.mutateAsync(toAbsenceInput(values));
              setImpact({ saved: true, impact: saved.impact });
              setMessage(t('me.absences.saved'));
              reset(emptyAbsenceForm());
            } catch {
              // O erro fica no ProblemAlert abaixo.
            }
          })(event);
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field id="absence-kind" label={t('me.absences.kind')}>
            <NativeSelect id="absence-kind" {...register('kind')}>
              {ABSENCE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`common.absenceKind.${kind}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field
            id="absence-start"
            label={t('me.absences.startDate')}
            error={translate(errors.startDate?.message)}
          >
            <Input
              id="absence-start"
              type="date"
              aria-invalid={Boolean(errors.startDate)}
              {...register('startDate')}
            />
          </Field>
          <Field
            id="absence-end"
            label={t('me.absences.endDate')}
            error={translate(errors.endDate?.message)}
          >
            <Input
              id="absence-end"
              type="date"
              aria-invalid={Boolean(errors.endDate)}
              {...register('endDate')}
            />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field
              id="absence-start-time"
              label={t('me.absences.startTime')}
              error={translate(errors.startTime?.message)}
            >
              <Input
                id="absence-start-time"
                type="time"
                aria-invalid={Boolean(errors.startTime)}
                {...register('startTime')}
              />
            </Field>
            <Field
              id="absence-end-time"
              label={t('me.absences.endTime')}
              error={translate(errors.endTime?.message)}
            >
              <Input
                id="absence-end-time"
                type="time"
                aria-invalid={Boolean(errors.endTime)}
                {...register('endTime')}
              />
            </Field>
          </div>
        </div>
        <p className="text-caption text-muted-foreground">{t('me.absences.timeHint')}</p>
        <label className="flex min-h-8 items-center gap-2 text-body">
          <NativeCheckbox {...register('blocksScheduling')} />
          {t('me.absences.blocks')}
        </label>
        <Field
          id="absence-note"
          label={t('me.absences.note')}
          error={translate(errors.note?.message)}
        >
          <Textarea id="absence-note" rows={2} maxLength={1000} {...register('note')} />
        </Field>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={preview.isPending}
            data-testid="my-absence-preview"
            onClick={() => {
              setMessage(null);
              void trigger().then(async (valid) => {
                if (!valid) {
                  return;
                }
                try {
                  const result = await preview.mutateAsync(toAbsenceInput(getValues()));
                  setImpact({ saved: false, impact: result });
                } catch {
                  // O erro fica no ProblemAlert abaixo.
                }
              });
            }}
          >
            {t('me.absences.preview')}
          </Button>
          <Button type="submit" disabled={create.isPending} data-testid="my-absence-save">
            {t('me.absences.save')}
          </Button>
          {message && (
            <p role="status" className="text-body" data-testid="my-absences-message">
              {message}
            </p>
          )}
        </div>
        {impact && <ImpactNote impact={impact.impact} saved={impact.saved} />}
        {create.isError && <ProblemAlert error={create.error} />}
        {preview.isError && <ProblemAlert error={preview.error} />}
      </form>

      <div className="flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-body font-semibold">{t('me.absences.listTitle')}</h3>
          <label className="flex min-h-8 items-center gap-2 text-body">
            <NativeCheckbox
              checked={includePast}
              onChange={(event) => {
                setIncludePast(event.target.checked);
              }}
            />
            {t('me.absences.includePast')}
          </label>
        </div>
        {absences.isError ? (
          <ProblemAlert
            error={absences.error}
            onRetry={() => {
              void absences.refetch();
            }}
          />
        ) : absences.isLoading || !absences.data ? (
          <Skeleton className="h-16 w-full" />
        ) : absences.data.length === 0 ? (
          <p className="text-body text-muted-foreground" data-testid="my-absences-empty">
            {t('me.absences.empty')}
          </p>
        ) : (
          <Table regionLabel={t('me.absences.tableLabel')} data-testid="my-absences-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('me.absences.columns.kind')}</TableHead>
                <TableHead>{t('me.absences.columns.period')}</TableHead>
                <TableHead>{t('me.absences.columns.blocks')}</TableHead>
                <TableHead>{t('me.absences.columns.note')}</TableHead>
                <TableHead>
                  <span className="sr-only">{t('me.absences.columns.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {absences.data.map((item) => (
                <TableRow key={item.id} data-testid={`my-absence-${item.id}`}>
                  <TableCell>
                    {t(`common.absenceKind.${item.kind}`, { defaultValue: item.kind })}
                  </TableCell>
                  <TableCell>
                    {civil(item.startDate)}
                    {item.endDate !== item.startDate ? ` → ${civil(item.endDate)}` : ''}
                    {item.startTime && item.endTime
                      ? ` · ${shortTime(item.startTime)}–${shortTime(item.endTime)}`
                      : ''}
                  </TableCell>
                  <TableCell>
                    <Badge variant={item.blocksScheduling ? 'warning' : 'outline'}>
                      {item.blocksScheduling
                        ? t('me.absences.blocksYes')
                        : t('me.absences.blocksNo')}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-72 truncate">{item.note ?? '—'}</TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        remove.reset();
                        setMessage(null);
                        setRemoving(item);
                      }}
                    >
                      {t('me.absences.remove')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <RemoveDialog
        absence={removing}
        pending={remove.isPending}
        error={remove.error}
        onCancel={() => {
          remove.reset();
          setRemoving(null);
        }}
        onConfirm={(absence) => {
          remove
            .mutateAsync({ absenceId: absence.id })
            .then(() => {
              setRemoving(null);
              setMessage(t('me.absences.removed'));
            })
            .catch(() => {
              // O erro fica no diálogo.
            });
        }}
      />
    </section>
  );
}

/** Prévia de impacto: indisponível até o agendamento existir (`schedulingAvailable=false`) — nunca "nada afetado". */
function ImpactNote({
  impact,
  saved,
}: {
  readonly impact: AbsenceImpact;
  readonly saved: boolean;
}) {
  const { t } = useTranslation('people');
  const dates = useCivilDate();
  return (
    <div className="rounded border p-2 text-body" role="status" data-testid="my-absence-impact">
      <p className="font-medium">
        {saved ? t('me.absences.impact.savedTitle') : t('me.absences.impact.title')}
      </p>
      {!impact.schedulingAvailable ? (
        <p className="text-muted-foreground" data-testid="my-absence-impact-unavailable">
          {t('me.absences.impact.unavailable')}
        </p>
      ) : impact.appointments.length === 0 ? (
        <p className="text-muted-foreground">{t('me.absences.impact.none')}</p>
      ) : (
        <>
          <p>{t('me.absences.impact.count', { count: impact.appointments.length })}</p>
          <ul className="list-disc pl-5">
            {impact.appointments.map((appointment) => (
              <li key={appointment.appointmentId}>{dates(appointment.scheduledAt.slice(0, 10))}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function RemoveDialog({
  absence,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  readonly absence: Absence | null;
  readonly pending: boolean;
  readonly error: unknown;
  readonly onCancel: () => void;
  readonly onConfirm: (absence: Absence) => void;
}) {
  const { t } = useTranslation('people');
  const { t: tc } = useTranslation('common');
  return (
    <Dialog
      open={absence !== null}
      onOpenChange={(open) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent closeLabel={tc('a11y.closeDialog')} data-testid="my-absence-remove-dialog">
        <DialogHeader>
          <DialogTitle>{t('me.absences.removeDialog.title')}</DialogTitle>
          <DialogDescription>{t('me.absences.removeDialog.description')}</DialogDescription>
        </DialogHeader>
        {error !== null && error !== undefined && <ProblemAlert error={error} />}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {tc('actions.cancel')}
          </Button>
          <Button
            type="button"
            disabled={pending}
            data-testid="my-absence-remove-confirm"
            onClick={() => {
              if (absence) {
                onConfirm(absence);
              }
            }}
          >
            {t('me.absences.removeDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
